import { useEffect, type ReactNode } from "react";
import { Link } from "@inertiajs/react";
import { ChevronRight, ArrowLeft } from "lucide-react";
import { cn } from "@/lib/cn";

/*
 * DetailTemplate — page chrome for a record detail: back link, breadcrumbs,
 * header (leading icon, title, meta badges, actions), optional sticky strip
 * (e.g. tabs), then the scroll body. Sections compose the body freely.
 */

export interface BreadcrumbItem {
  label: string;
  to?: string;
}

export interface DetailTemplateProps {
  title: string;
  subtitle?: ReactNode;
  breadcrumbs?: BreadcrumbItem[];
  backTo?: string;
  backLabel?: string;
  leading?: ReactNode;
  meta?: ReactNode;
  primaryAction?: ReactNode;
  actionsMenu?: ReactNode;
  belowHeader?: ReactNode;
  wide?: boolean;
  children: ReactNode;
}

export function DetailTemplate({
  title,
  subtitle,
  breadcrumbs,
  backTo,
  backLabel = "Back",
  leading,
  meta,
  primaryAction,
  actionsMenu,
  belowHeader,
  wide = false,
  children,
}: DetailTemplateProps) {
  useEffect(() => {
    const previous = document.title;
    document.title = `${title} · stabiliti`;
    return () => {
      document.title = previous;
    };
  }, [title]);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="border-b border-border-subtle bg-canvas px-4 pt-4 pb-3 sm:px-6 sm:pt-5 sm:pb-4">
        {((breadcrumbs && breadcrumbs.length > 0) || backTo) && (
          <div className="mb-3 flex items-center gap-3 text-xs text-fg-subtle">
            {backTo && (
              <Link href={backTo} className="inline-flex items-center gap-1 hover:text-fg transition-colors">
                <ArrowLeft className="size-3.5" />
                {backLabel}
              </Link>
            )}
            {backTo && breadcrumbs && breadcrumbs.length > 0 && (
              <span className="h-4 w-px bg-border-subtle" />
            )}
            {breadcrumbs && breadcrumbs.length > 0 && (
              <nav aria-label="Breadcrumb" className="flex items-center gap-1">
                {breadcrumbs.map((bc, i) => (
                  <span key={i} className="flex items-center gap-1">
                    {i > 0 && <ChevronRight className="size-3" />}
                    {bc.to ? (
                      <Link href={bc.to} className="hover:text-fg">{bc.label}</Link>
                    ) : (
                      <span className={cn(i === breadcrumbs.length - 1 && "text-fg")}>{bc.label}</span>
                    )}
                  </span>
                ))}
              </nav>
            )}
          </div>
        )}

        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
          <div className="flex min-w-0 items-center gap-3">
            {leading}
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="truncate text-xl font-semibold tracking-tight text-fg">{title}</h1>
                {meta}
              </div>
              {subtitle && <div className="mt-0.5 truncate text-sm text-fg-muted">{subtitle}</div>}
            </div>
          </div>
          <div className="flex items-center gap-2 sm:shrink-0">
            {actionsMenu}
            {primaryAction}
          </div>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col">
        {belowHeader && <div className="z-10 shrink-0 bg-canvas px-4 sm:px-6">{belowHeader}</div>}
        <div
          className={cn(
            "min-h-0 w-full flex-1 overflow-auto px-4 py-5 sm:px-6 sm:py-6",
            wide ? "" : "mx-auto max-w-[1280px]",
          )}
        >
          {children}
        </div>
      </div>
    </div>
  );
}
